import * as React from "react"

import {
  PINE_DETAIL_DIALOG_CLOSE_MS,
  StrategyPineDetailDialog,
} from "@/components/Strategies/StrategyPineDetailDialog"
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount"
import {
  fetchBrokerCapabilities,
  type BrokerCapabilities,
} from "@/services/brokerIntegrationApi"
import { clearBinanceHistoryPath } from "@/utils/binanceTradeSync"
import { clearBrokerHistoryPath } from "@/utils/brokerTradeSync"
import { clearCtraderHistoryPath } from "@/utils/ctraderOpenApiSync"
import { resolveBrokerDisplayName } from "@/utils/customCsvAccountDisplay"
import { presentMutationError } from "@/utils/showApiErrorToast"
import {
  defaultTradingViewLane,
  findTradingViewLaneCapability,
  isTradingViewConnectBrandSlug,
  TRADINGVIEW_CONNECT_BRAND,
  type TradingViewLaneSlug,
} from "@/utils/tradingViewConnectBrand"
import BrokerIntegrationSection, {
  type EmbeddedSetupBackHandle,
  type EmbeddedWizardFooterAction,
} from "@/views/Dashboard/Profile/components/BrokerIntegrationSection"
import { ConnectBrokerChooseStep } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerChooseStep"
import { ConnectBrokerManualEntryStep } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerManualEntryStep"
import { ConnectBrokerSelectImportMethodStep } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerSelectImportMethodStep"
import { ConnectBrokerTradingViewLaneStep } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerTradingViewLaneStep"
import type { ConnectBrokerImportMethodId } from "@/views/Dashboard/Profile/connectBroker/connectBrokerImportMethods"
import { resolveConnectBrokerImportMethods } from "@/views/Dashboard/Profile/connectBroker/connectBrokerImportMethods"
import {
  ConnectBrokerWizardDialogFooter,
  type ConnectBrokerWizardFooterAction,
} from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerWizardLayout"
import {
  normalizeConnectBrokerSlug,
  type ConnectBrokerWizardOpenSeed,
} from "@/views/Dashboard/Profile/connectBroker/connectBrokerWizardConstants"
import { isMt4Broker } from "@/utils/mt4Broker"
import { isMt5Broker } from "@/utils/mt5Broker"
import { reportPageFieldFocusSx } from "@/views/Dashboard/Reports/reportPageFieldFocus"

type ConnectBrokerWizardStep = 1 | 2 | 3

export type ConnectBrokerWizardDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional deep-link / Import trades seed applied when the dialog opens. */
  seed?: ConnectBrokerWizardOpenSeed | null
}

type StepPresentation = {
  title: string
  description: string
  onBack: () => void
  onContinue?: () => void
  continueDisabled?: boolean
  continueLabel?: string
  showContinue?: boolean
  showBack?: boolean
  /** When false, title/description render in the step body (selected-broker heading). */
  showHeader?: boolean
  primaryFooterAction?: ConnectBrokerWizardFooterAction
  body: React.ReactNode
}

function emptyWizardState() {
  return {
    step: 1 as ConnectBrokerWizardStep,
    selectedBrokerName: null as string | null,
    selectedTradingViewLane: null as TradingViewLaneSlug | null,
    selectedImportMethod: null as ConnectBrokerImportMethodId | null,
  }
}

export function ConnectBrokerWizardDialog({
  open,
  onOpenChange,
  seed = null,
}: ConnectBrokerWizardDialogProps): React.ReactElement {
  const { accounts: tradeAccountOptions } = useActiveBrokerAccount()
  const [step, setStep] = React.useState<ConnectBrokerWizardStep>(1)
  const [brokers, setBrokers] = React.useState<BrokerCapabilities[]>([])
  const [loadingBrokers, setLoadingBrokers] = React.useState(true)
  const [selectedBrokerName, setSelectedBrokerName] = React.useState<string | null>(null)
  const [selectedTradingViewLane, setSelectedTradingViewLane] =
    React.useState<TradingViewLaneSlug | null>(null)
  const [selectedImportMethod, setSelectedImportMethod] =
    React.useState<ConnectBrokerImportMethodId | null>(null)
  const embeddedSetupBackRef = React.useRef<EmbeddedSetupBackHandle | null>(null)
  const [embeddedFooterAction, setEmbeddedFooterAction] =
    React.useState<ConnectBrokerWizardFooterAction | null>(null)
  const seedHandledRef = React.useRef(false)
  const seedKeyRef = React.useRef<string>("")

  const resetWizard = React.useCallback(() => {
    const next = emptyWizardState()
    setStep(next.step)
    setSelectedBrokerName(next.selectedBrokerName)
    setSelectedTradingViewLane(next.selectedTradingViewLane)
    setSelectedImportMethod(next.selectedImportMethod)
    setEmbeddedFooterAction(null)
    seedHandledRef.current = false
    clearBinanceHistoryPath()
    clearBrokerHistoryPath("ibkr")
    clearCtraderHistoryPath()
  }, [])

  React.useEffect(() => {
    if (step !== 3) {
      setEmbeddedFooterAction(null)
    }
  }, [step])

  React.useEffect(() => {
    if (open) return
    const timer = window.setTimeout(() => {
      resetWizard()
    }, PINE_DETAIL_DIALOG_CLOSE_MS)
    return () => window.clearTimeout(timer)
  }, [open, resetWizard])

  const handleEmbeddedWizardFooterChange = React.useCallback(
    (action: EmbeddedWizardFooterAction | null) => {
      setEmbeddedFooterAction(action)
    },
    []
  )

  const handleEmbeddedWizardImportMethodChange = React.useCallback(
    (method: ConnectBrokerImportMethodId) => {
      setSelectedImportMethod(method)
    },
    []
  )

  const handleEmbeddedWizardComplete = React.useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const isTradingViewBrandSelected =
    selectedBrokerName === TRADINGVIEW_CONNECT_BRAND ||
    isTradingViewConnectBrandSlug(selectedBrokerName)

  const setupBrokerSlug =
    selectedTradingViewLane ??
    (isTradingViewConnectBrandSlug(selectedBrokerName) &&
    selectedBrokerName !== TRADINGVIEW_CONNECT_BRAND
      ? (selectedBrokerName as TradingViewLaneSlug)
      : null) ??
    (!isTradingViewBrandSelected ? selectedBrokerName : null)

  const selectedCapability = React.useMemo(() => {
    if (!setupBrokerSlug) return null
    return (
      brokers.find(
        (broker) =>
          normalizeConnectBrokerSlug(broker.broker_name) ===
          normalizeConnectBrokerSlug(setupBrokerSlug)
      ) ?? null
    )
  }, [brokers, setupBrokerSlug])

  const connectedBrokerSlugs = React.useMemo(() => {
    const slugs = new Set<string>()
    tradeAccountOptions.forEach((account) => {
      const slug = normalizeConnectBrokerSlug(account.brokerName || "")
      if (slug) slugs.add(slug)
    })
    return slugs
  }, [tradeAccountOptions])

  React.useEffect(() => {
    if (!open) return
    let cancelled = false

    const loadBrokers = async () => {
      setLoadingBrokers(true)
      try {
        const list = await fetchBrokerCapabilities()
        if (!cancelled) {
          setBrokers(list)
        }
      } catch (error) {
        if (!cancelled) {
          presentMutationError(error, "Failed to load brokers")
        }
      } finally {
        if (!cancelled) {
          setLoadingBrokers(false)
        }
      }
    }

    void loadBrokers()
    return () => {
      cancelled = true
    }
  }, [open])

  /** Apply Import trades / deep-link seed once brokers are loaded. */
  React.useEffect(() => {
    if (!open || loadingBrokers) return

    const nextSeedKey = `${seed?.brokerName ?? ""}|${seed?.importMethod ?? ""}`
    if (seedKeyRef.current !== nextSeedKey) {
      seedKeyRef.current = nextSeedKey
      seedHandledRef.current = false
    }
    if (seedHandledRef.current) return

    const brokerParam = (seed?.brokerName || "").trim()
    if (!brokerParam) {
      seedHandledRef.current = true
      return
    }

    const brokerSlug = normalizeConnectBrokerSlug(brokerParam)
    const broker = brokers.find(
      (item) => normalizeConnectBrokerSlug(item.broker_name) === brokerSlug
    )
    if (!broker) {
      seedHandledRef.current = true
      return
    }

    const methodParam = (seed?.importMethod || "") as ConnectBrokerImportMethodId | ""
    const availableMethods = resolveConnectBrokerImportMethods(broker)
    const resolvedMethod =
      methodParam && availableMethods.some((method) => method.id === methodParam)
        ? methodParam
        : availableMethods.find((method) => method.id === "file_upload")?.id ??
          availableMethods[0]?.id ??
          null

    seedHandledRef.current = true

    if (isTradingViewConnectBrandSlug(brokerSlug)) {
      const lane =
        defaultTradingViewLane(brokers, brokerSlug) ??
        (brokerSlug as TradingViewLaneSlug)
      setSelectedBrokerName(TRADINGVIEW_CONNECT_BRAND)
      setSelectedTradingViewLane(lane)
      if (resolvedMethod) {
        setSelectedImportMethod(resolvedMethod)
        setStep(methodParam || availableMethods.length === 1 ? 3 : 2)
      } else {
        setSelectedImportMethod("file_upload")
        setStep(2)
      }
    } else {
      setSelectedBrokerName(broker.broker_name)
      setSelectedTradingViewLane(null)
      if (resolvedMethod) {
        setSelectedImportMethod(resolvedMethod)
        setStep(methodParam || availableMethods.length === 1 ? 3 : 2)
      } else {
        setStep(2)
      }
    }
  }, [brokers, loadingBrokers, open, seed])

  const closeDialog = React.useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleSelectBroker = React.useCallback((brokerName: string) => {
    setSelectedBrokerName(brokerName)
    setSelectedTradingViewLane(null)
  }, [])

  const handleContinueFromChoose = React.useCallback(() => {
    if (!selectedBrokerName) return
    setSelectedImportMethod(null)
    if (selectedBrokerName === TRADINGVIEW_CONNECT_BRAND) {
      const lane = defaultTradingViewLane(brokers)
      setSelectedTradingViewLane(lane)
      setStep(2)
      return
    }
    setStep(2)
  }, [brokers, selectedBrokerName])

  const handleBackFromStep2 = React.useCallback(() => {
    setSelectedImportMethod(null)
    setSelectedTradingViewLane(null)
    if (isTradingViewConnectBrandSlug(selectedBrokerName)) {
      setSelectedBrokerName(TRADINGVIEW_CONNECT_BRAND)
    }
    setStep(1)
  }, [selectedBrokerName])

  const handleContinueFromImportMethod = React.useCallback(() => {
    if (!selectedImportMethod) return
    setStep(3)
  }, [selectedImportMethod])

  const handleContinueFromTradingViewLane = React.useCallback(() => {
    const lane = selectedTradingViewLane ?? defaultTradingViewLane(brokers)
    if (!lane || !findTradingViewLaneCapability(brokers, lane)) return
    setSelectedTradingViewLane(lane)
    setSelectedImportMethod("file_upload")
    setStep(3)
  }, [brokers, selectedTradingViewLane])

  const handleBackFromSetup = React.useCallback(() => {
    if (embeddedSetupBackRef.current?.tryHandleBack()) {
      return
    }
    clearBinanceHistoryPath()
    clearBrokerHistoryPath("ibkr")
    clearCtraderHistoryPath()
    if (selectedTradingViewLane) {
      setSelectedBrokerName(TRADINGVIEW_CONNECT_BRAND)
      setSelectedImportMethod(null)
    }
    setStep(2)
  }, [selectedTradingViewLane])

  const presentation = React.useMemo((): StepPresentation => {
    if (step === 1) {
      return {
        title: "Connect a broker",
        description:
          "Choose the broker you want to integrate or import trades from.",
        onBack: closeDialog,
        onContinue: handleContinueFromChoose,
        continueDisabled: !selectedBrokerName || loadingBrokers,
        body: (
          <ConnectBrokerChooseStep
            brokers={brokers}
            connectedBrokerSlugs={connectedBrokerSlugs}
            selectedBrokerName={
              isTradingViewConnectBrandSlug(selectedBrokerName)
                ? TRADINGVIEW_CONNECT_BRAND
                : selectedBrokerName
            }
            onSelectBroker={handleSelectBroker}
            loading={loadingBrokers}
          />
        ),
      }
    }

    if (step === 2 && selectedBrokerName === TRADINGVIEW_CONNECT_BRAND) {
      return {
        title: "Choose TradingView path",
        description:
          "Paper Trading and Backtesting use different CSV exports. Select the path that matches your file.",
        showHeader: false,
        onBack: handleBackFromStep2,
        onContinue: handleContinueFromTradingViewLane,
        continueDisabled: !selectedTradingViewLane,
        showBack: true,
        body: (
          <ConnectBrokerTradingViewLaneStep
            brokers={brokers}
            selectedLane={selectedTradingViewLane}
            onSelectLane={setSelectedTradingViewLane}
          />
        ),
      }
    }

    if (step === 2 && selectedCapability && !isTradingViewBrandSelected) {
      return {
        title: "Select import method",
        description: "Pick how you want to bring trades into Tessera for this broker.",
        showHeader: false,
        onBack: handleBackFromStep2,
        onContinue: handleContinueFromImportMethod,
        continueDisabled: !selectedImportMethod,
        showBack: true,
        body: (
          <ConnectBrokerSelectImportMethodStep
            capability={selectedCapability}
            selectedMethodId={selectedImportMethod}
            onSelectMethod={setSelectedImportMethod}
          />
        ),
      }
    }

    if (step === 3 && selectedCapability && selectedImportMethod) {
      const brokerTitle = resolveBrokerDisplayName(selectedCapability)
      const embeddedBrokerName =
        selectedTradingViewLane ?? selectedCapability.broker_name

      if (selectedImportMethod === "manual") {
        return {
          title: `Add trades for ${brokerTitle}`,
          description: "Create your account label and enter trades one row at a time.",
          onBack: handleBackFromSetup,
          showContinue: false,
          showBack: true,
          body: <ConnectBrokerManualEntryStep capability={selectedCapability} />,
        }
      }

      const isAutoSyncSetup = selectedImportMethod === "auto_sync"
      const isFileUploadSetup = selectedImportMethod === "file_upload"
      const usesEmbeddedFooter = isAutoSyncSetup || isFileUploadSetup
      const fileUploadDescription = isMt5Broker(embeddedBrokerName)
        ? "Upload your MetaTrader 5 account history XLSX below."
        : isMt4Broker(embeddedBrokerName)
          ? "Upload your MetaTrader 4 Detailed Report HTML (.htm or .html) below."
          : "Upload your broker statement or trade history file below."

      return {
        title: isAutoSyncSetup
          ? `Connect ${brokerTitle}`
          : `Import trades for ${brokerTitle}`,
        description: isAutoSyncSetup
          ? "Complete the steps below to connect and sync trades automatically."
          : fileUploadDescription,
        onBack: handleBackFromSetup,
        showContinue: false,
        showBack: true,
        primaryFooterAction: usesEmbeddedFooter
          ? embeddedFooterAction ?? undefined
          : undefined,
        body: (
          <BrokerIntegrationSection
            embeddedInConnectWizard
            embeddedBrokerName={embeddedBrokerName}
            embeddedImportMethod={selectedImportMethod}
            embeddedSetupBackRef={embeddedSetupBackRef}
            onEmbeddedWizardFooterChange={handleEmbeddedWizardFooterChange}
            onEmbeddedWizardImportMethodChange={handleEmbeddedWizardImportMethodChange}
            onEmbeddedWizardComplete={handleEmbeddedWizardComplete}
          />
        ),
      }
    }

    return {
      title: "Connect a broker",
      description: "Choose a broker to continue.",
      onBack: closeDialog,
      onContinue: () => setStep(1),
      continueLabel: "Start over",
      body: (
        <p className="text-sm text-muted-foreground">
          Missing broker selection. Start over to continue.
        </p>
      ),
    }
  }, [
    brokers,
    closeDialog,
    connectedBrokerSlugs,
    embeddedFooterAction,
    handleBackFromSetup,
    handleBackFromStep2,
    handleContinueFromChoose,
    handleContinueFromImportMethod,
    handleContinueFromTradingViewLane,
    handleEmbeddedWizardComplete,
    handleEmbeddedWizardFooterChange,
    handleEmbeddedWizardImportMethodChange,
    handleSelectBroker,
    isTradingViewBrandSelected,
    loadingBrokers,
    selectedBrokerName,
    selectedCapability,
    selectedImportMethod,
    selectedTradingViewLane,
    step,
  ])

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={onOpenChange}
      title={presentation.title}
      description={presentation.description}
      size="wide"
      contentClassName="sm:max-w-5xl lg:max-w-6xl max-h-[min(92vh,56rem)]"
      bodyClassName="px-1"
      showDismissButton={false}
      showHeader={presentation.showHeader !== false}
      contentProps={{
        "data-connect-broker-wizard": "",
        "data-connect-broker-wizard-dialog": "",
        style: reportPageFieldFocusSx as React.CSSProperties,
      }}
      footer={
        <ConnectBrokerWizardDialogFooter
          onCancel={closeDialog}
          onBack={presentation.onBack}
          onContinue={presentation.onContinue}
          continueDisabled={presentation.continueDisabled}
          continueLabel={presentation.continueLabel}
          showContinue={presentation.showContinue}
          showBack={presentation.showBack}
          primaryFooterAction={presentation.primaryFooterAction}
        />
      }
    >
      {presentation.body}
    </StrategyPineDetailDialog>
  )
}
